import Player from '@vimeo/player'
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { getValidVideo } from '../utilities/video'
import { Image, ImageProps } from './Image'
import { PauseButton } from './PauseButton'

export interface VimeoVideoProps {
  className?: string
  hideControlsAndAutoplay?: boolean
  imgElementClassName?: string
  loading?: 'eager' | 'lazy'
  muted?: boolean
  onBackgroundVideoStarted?: () => void
  /**
   * For controlling pause state from outside this component
   */
  paused?: boolean
  previewImage?: ImageProps
  previewImageClassName?: string
  /**
   * Pause button is only available with hideControlsAndAutoplay
   */
  showPauseButton?: boolean
  title: string
  // Allow undefined here to potentially only displaying a previewImage
  videoUrl: string | undefined
}

export const VimeoVideo = ({
  className,
  hideControlsAndAutoplay = true,
  imgElementClassName,
  loading,
  muted = true,
  onBackgroundVideoStarted,
  paused,
  previewImage,
  previewImageClassName,
  showPauseButton,
  title,
  videoUrl,
}: VimeoVideoProps): JSX.Element | null => {
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [backgroundVideoStarted, setBackgroundVideoStarted] = useState(false)
  const iframeRef = useRef(null)
  const playerRef = useRef<Player>()

  // variables for iphone hack (see useEffect further down)
  const intentionalPauseRef = useRef(true)
  const [wasPausedByIphone, setWasPausedByIphone] = useState(false)
  const [videoId, setVideoId] = useState(0)

  useEffect(() => {
    if (hideControlsAndAutoplay && iframeRef.current) {
      const player = new Player(iframeRef.current)
      playerRef.current = player

      const onPlay = () => {
        if (intentionalPauseRef.current === false) {
          setWasPausedByIphone(true)
        }
        intentionalPauseRef.current = false
        setBackgroundVideoStarted(true)
      }

      player.on('play', onPlay)
      player.on('loaded', e => {
        setVideoId(e.id)
      })

      return () => {
        if (player) {
          player.off('play', onPlay)
          player.off('loaded')
          player.destroy()
          playerRef.current = undefined
        }
      }
    }
  }, [hideControlsAndAutoplay])

  useEffect(() => {
    ;(async () => {
      // HACK: iPhone will pause the video when switching tabs in Safari.
      // Safari resumes the video when the tab is re-opened, but the video
      // is invisible (the player can play and pause and tracks progress,
      // but there is no visible video). A workaround is to reload the video.
      // This hack only works if the video was playing.
      if (wasPausedByIphone && playerRef.current && videoId > 0) {
        intentionalPauseRef.current = true // player pauses on reload
        await playerRef.current.loadVideo(videoId)
        setWasPausedByIphone(false)
      }
    })()
  }, [wasPausedByIphone, videoId])

  useEffect(() => {
    // let parent component know when to show external pause button
    if (backgroundVideoStarted) {
      onBackgroundVideoStarted?.()
    }
  }, [backgroundVideoStarted, onBackgroundVideoStarted])

  useEffect(() => {
    ;(async () => {
      // pause state passed from parent component
      if (hideControlsAndAutoplay && playerRef.current) {
        if (paused) {
          intentionalPauseRef.current = true
          playerRef.current.pause()
        } else {
          try {
            await playerRef.current.play()
          } catch (e) {
            console.warn(e)
          }
        }
      }
    })()
  }, [paused, hideControlsAndAutoplay])

  useEffect(() => {
    ;(async () => {
      if (playerRef.current) {
        await playerRef.current.setMuted(muted)
      }
    })()
  }, [muted])

  const onPause = async () => {
    if (playerRef.current) {
      const isPaused = await playerRef.current.getPaused()

      if (isPaused) {
        try {
          await playerRef.current.play()
        } catch (e) {
          console.warn(e)
        }
      } else {
        intentionalPauseRef.current = true
        playerRef.current.pause()
      }
    }
  }

  const video = getValidVideo(videoUrl, ['vimeo'])
  const showPreviewImage =
    !iframeLoaded || (hideControlsAndAutoplay && !backgroundVideoStarted)

  if (video && hideControlsAndAutoplay) {
    video.url.searchParams.append('background', '1')
    video.url.searchParams.append('loop', '1')
    video.url.searchParams.append('autopause', '0')
  }

  return (
    <div
      className={classNames(className ?? 'w-full h-full', {
        'relative': showPauseButton && !className,
      })}
    >
      {video && (
        <>
          <iframe
            allow="autoplay"
            className={classNames('outline-none w-full h-full', {
              'pointer-events-none': hideControlsAndAutoplay,
            })}
            loading={loading}
            onLoad={() => setIframeLoaded(true)}
            ref={iframeRef}
            src={video.url.toString()}
            tabIndex={hideControlsAndAutoplay ? -1 : undefined}
            title={title}
          />

          {showPauseButton && backgroundVideoStarted && (
            <PauseButton
              additionalClassName="absolute z-10 top-0 right-0 m-15"
              onClick={onPause}
              size="small"
            />
          )}
        </>
      )}

      {previewImage && (
        <Image
          {...previewImage}
          className={classNames(
            'pointer-events-none transition-opacity duration-700 ease-out',
            showPreviewImage ? 'opacity-100' : 'opacity-0',
            previewImageClassName ?? 'absolute inset-0'
          )}
          imgClassName={imgElementClassName}
          caption=""
          copyright=""
        />
      )}
    </div>
  )
}
